import {
  AGENT_STATE,
  OUTCOME,
  pausedByText,
  plainRunError,
  relativeTime,
} from '@/modules/agents/utils/agentStatusLabels';
import type { AgencyAgentStatus } from '../../api/agentStatusTypes';
import { TONE_CLASS } from './tone';
import styles from './AgentStatus.module.css';

function sentence(status: AgencyAgentStatus, agentName: string, now: number): string {
  const run = status.run;
  switch (status.state) {
    case 'not_hired':
      return `${agentName} is not hired here.`;
    case 'paused':
      return pausedByText(status.pausedBy);
    case 'unknown':
      return `${agentName}'s runtime could not be reached, so its state is unknown.`;
    case 'working':
      return run?.activeRun?.taskTitle
        ? `Working on ${run.activeRun.taskTitle} since ${relativeTime(run.activeRun.since, now)}.`
        : `Working since ${relativeTime(run?.activeRun?.since ?? now, now)}.`;
    case 'needs_attention':
      return status.reasons.length === 1
        ? '1 thing needs a look:'
        : `${status.reasons.length} things need a look:`;
    case 'up_to_date':
      return run?.next
        ? `Nothing due. Next: ${run.next.title} ${relativeTime(run.next.at, now)}.`
        : 'Nothing due and nothing scheduled.';
  }
}

/**
 * One state, one sentence, and the reasons behind "Needs attention". The state and the Run log link
 * open the run log; a reason about a run opens it at that run, a reason about a task opens the task.
 */
export function AgentStatusLine({
  status,
  isLoading,
  agentName,
  onOpenRunLog,
  onOpenClientFacts,
  onOpenObservations,
  onFocusTask,
}: {
  status: AgencyAgentStatus | undefined;
  isLoading: boolean;
  agentName: string;
  onOpenRunLog: (runLogId?: string) => void;
  onOpenClientFacts: () => void;
  onOpenObservations: () => void;
  onFocusTask: (taskId: string) => void;
}) {
  if (!status) {
    return (
      <div className={styles.statusLine}>
        <p className={styles.quiet}>{isLoading ? 'Loading status…' : 'Status is unavailable.'}</p>
      </div>
    );
  }
  const now = Date.now();
  const state = AGENT_STATE[status.state];
  const run = status.run;
  const lastWake = run?.lastWake;

  return (
    <div className={styles.statusLine} role="status" aria-label={`${agentName}'s status`}>
      <div className={styles.statusHead}>
        <button
          type="button"
          className={styles.stateButton}
          title="Open the run log"
          onClick={() => onOpenRunLog(status.reasons.find((reason) => reason.runLogId)?.runLogId)}
        >
          <span className={TONE_CLASS[state.tone]}>{state.label}</span>
        </button>
        <span className={styles.statusSentence}>{sentence(status, agentName, now)}</span>
        <span className={styles.statusLinks}>
          <button type="button" className={styles.linkButton} onClick={() => onOpenRunLog()}>
            Run log
          </button>
          <button type="button" className={styles.linkButton} onClick={onOpenClientFacts}>
            Client facts
          </button>
          <button type="button" className={styles.linkButton} onClick={onOpenObservations}>
            Observations
          </button>
        </span>
      </div>
      {status.reasons.length > 0 && (
        <ul className={styles.reasons}>
          {status.reasons.map((reason, index) => (
            <li key={`${reason.kind}:${index}`}>
              {reason.text}
              {reason.error ? `: ${plainRunError(reason.error, agentName, reason.draftSaved)}` : ''}
              {reason.runLogId && (
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => onOpenRunLog(reason.runLogId)}
                >
                  See the run
                </button>
              )}
              {!reason.runLogId && reason.taskId && (
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => onFocusTask(reason.taskId!)}
                >
                  See the task
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {run && (
        <div className={styles.chips}>
          <span>
            <strong>Last woke</strong>{' '}
            {lastWake
              ? `${relativeTime(lastWake.at, now)} · ${lastWake.outcome === 'idle' ? 'nothing to do' : OUTCOME[lastWake.outcome].label.toLowerCase()}`
              : 'not yet'}
          </span>
          <span>
            <strong>Last successful run</strong>{' '}
            {run.lastSuccessAt ? relativeTime(run.lastSuccessAt, now) : 'none yet'}
          </span>
          {run.next && status.state !== 'up_to_date' && (
            <span>
              <strong>Next</strong> {run.next.title} {relativeTime(run.next.at, now)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
